import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器：添加 token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器：处理 401 错误（登录接口的 401 不跳转，仅提示错误）
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // 翻译错误信息
    let errorMsg = '请求失败';
    if (error.response) {
      const status = error.response.status;
      const detail = error.response.data?.detail;
      
      if (status === 401) {
        errorMsg = '登录已过期或未授权';
        const isLoginRequest = error.config?.url?.includes('/auth/login');
        if (!isLoginRequest) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('username');
          window.location.href = '/';
        }
      } else if (status === 403) {
        errorMsg = '没有权限执行此操作';
      } else if (status === 404) {
        errorMsg = '请求的资源不存在';
      } else if (status === 405) {
        errorMsg = '请求方法不允许 (Method Not Allowed)';
      } else if (status === 500) {
        errorMsg = detail || '服务器内部错误';
      } else if (detail) {
        errorMsg = detail; // 如果后端返回了具体错误信息，优先使用
      }
      
      // 覆盖 response data detail 以便下游组件显示中文
      if (error.response.data && typeof error.response.data === 'object') {
        error.response.data.detail = errorMsg;
      }
    } else if (error.request) {
      // 网络错误
      errorMsg = '网络连接失败，请检查网络或后端服务是否启动';
      error.message = errorMsg;
    } else {
      errorMsg = error.message;
    }

    // 可以选择在这里统一提示，或者修改 error 对象让组件提示
    // 为了不破坏现有组件的 try-catch 逻辑，我们主要修改 error 对象
    // 但对于 500/405 等严重错误，组件可能处理不好，这里可以加个 console log
    console.error('API Error:', errorMsg);

    return Promise.reject(error);
  }
);

export interface Agent {
  id: number;
  name: string;
  root_advertiser_id: string;
  app_id: string;
  has_token: boolean;
  advertiser_count: number;
}

export interface Advertiser {
  id: number;
  advertiser_id: string;
  uuid?: string;
  name: string;
  company_name?: string;
  industry: string;
  qual_status: number | null;
  agent_id?: number;
}

export interface Qualification {
  id: number;
  advertiser_id: number;
  advertiser_uuid?: string; // New

  advertiser_name: string;
  company_name?: string; // New
  qual_id: string;
  product_name?: string; // New
  product_type?: number; // New
  industry_first: string;
  industry_first_name?: string; // New
  industry_second: string;
  industry_second_name?: string; // New
  description: string;
  state: number;
  state_text: string;
  valid_date: string;
  image_urls: string[];
  reject_reason: string | null;
}

export interface Industry {
  id: string;
  name: string;
  parent_id: string | null;
  qualifications?: {
    industry: string;
    necessary: string;
    supplementary: string;
  };
}

// 代理商相关
export const getAgents = () => api.get<Agent[]>(`/api/agents`);

export const createAgent = (data: { name: string; root_advertiser_id?: string }) =>
  api.post<Agent>(`/api/agents`, data);

export const updateAgent = (id: number, data: { name?: string; root_advertiser_id?: string }) =>
  api.put<Agent>(`/api/agents/${id}`, data);

export const deleteAgent = (id: number) =>
  api.delete(`/api/agents/${id}`);

export const batchDeleteAgents = (agentIds: number[]) =>
  api.post<{ success: boolean; message: string }>(`/api/agents/batch_delete`, { agent_ids: agentIds });

export const importAgents = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post<{
    success: boolean;
    imported: number;
    skipped: number;
    errors: string[];
  }>(`/api/agents/import`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const getAuthUrl = (agentId: number) =>
  api.get<{ authorization_url: string; agent_id: number; agent_name: string }>(
    `/api/auth/url/${agentId}`
  );

export const getAgent = (agentId: number) => 
  api.get<Agent>(`/api/agents/${agentId}`);

export const getAdvertisers = (agentId: number, skip = 0, limit = 10000) =>
  api.get<Advertiser[]>(`/api/agents/${agentId}/advertisers`, {
    params: { skip, limit }
  });

export const getAdvertisersByAgents = (agentIds: number[], page = 1, pageSize = 10000) =>
  api.post<{
    total: number;
    items: Advertiser[];
  }>(`/api/agents/advertisers/list`, {
    agent_ids: agentIds,
    page,
    page_size: pageSize
  });

export const addAdvertiser = (agentId: number, data: {
  advertiser_id: string;
  name: string;
  industry?: string;
}) => api.post(`/api/agents/${agentId}/advertisers`, data);

export const importAdvertisers = (agentId: number, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post<{
    success: boolean;
    imported: number;
    skipped: number;
    errors: string[];
  }>(`/api/agents/${agentId}/advertisers/import`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const syncAdvertisers = (agentId: number) =>
  api.post<{
    success: boolean;
    synced_count: number;
    message: string;
    task_id: string;
  }>(`/api/agents/${agentId}/advertisers/sync`);

export const batchDeleteAdvertisers = (advertiserIds: number[]) =>
  api.post<{
    success: boolean;
    deleted_count: number;
  }>('/api/agents/advertisers/batch_delete', { advertiser_ids: advertiserIds });

// 资质相关
export const getQualList = (
  advertiserIds: number[],
  page = 1,
  pageSize = 20,
  statusFilter: number[] | null = null,
  extra: { industry_first?: string; industry_second?: string; product_name?: string } = {}
) =>
  api.post<{
    total: number;
    page: number;
    page_size: number;
    data: Qualification[];
  }>('/api/qual/list', {
    advertiser_ids: advertiserIds,
    page,
    page_size: pageSize,
    status_filter: statusFilter,
    industry_first: extra.industry_first || undefined,
    industry_second: extra.industry_second || undefined,
    product_name: extra.product_name || undefined,
  });

export const syncQualifications = (advertiserIds: number[]) =>
  api.post('/api/qual/sync', { advertiser_ids: advertiserIds });

export const uploadQualFile = (agentId: number, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post<{
    success: boolean;
    file_id: string;
    file_url: string;
    filename: string;
  }>(`/api/qual/upload?agent_id=${agentId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const addQualification = (data: {
  advertiser_id: number;
  industry_first: string;
  industry_second: string;
  product_name: string;
  product_type: number;
  description: string;
  qualifications: { url: string; type_id: number; type: number; name?: string }[];
  icp_case_number?: string;
  icp_domain_name?: string;
}) => api.post('/api/qual/add', data);

export const batchAddQualification = (data: {
  advertiser_ids: number[];
  industry_first: string;
  industry_second: string;
  product_name: string;
  product_type: number;
  description: string;
  qualifications: { url: string; type_id: number; type: number; name?: string }[];
  icp_case_number?: string;
  icp_domain_name?: string;
}) => api.post('/api/qual/batch_add', data);

export const batchDeleteQualifications = (qualificationIds: number[]) =>
  api.post<{
    success: boolean;
    success_count: number;
    failed_count: number;
    errors: string[];
  }>('/api/qual/batch_delete', { qualification_ids: qualificationIds });

export const batchEditQualifications = (edits: Array<{
  advertiser_id: number;
  qualifications: Array<{
      qual_id: string; // Vivo ID
      product_name?: string;
      product_type?: number;
      description?: string;
      qualifications?: { url: string; type_id: number; type: number; name?: string }[];
      icp_case_number?: string;
      icp_domain_name?: string;
  }>;
}>) => api.post('/api/qual/batch_edit', { edits });

export const getIndustries = (agentId: number, parentId?: string, advertiserId?: string) =>
  api.get<{
    success: boolean;
    industries: Industry[];
  }>('/api/qual/industries', {
    params: { agent_id: agentId, parent_id: parentId, advertiser_id: advertiserId }
  });

// 行业使用频次聚合（后端 GROUP BY，单接口 < 50ms；替代旧版「拉全量资质聚合」）
export interface IndustryUsageItem {
  id: string;
  name: string | null;
  count: number;
}
export const getIndustryUsage = (agentId: number) =>
  api.get<{
    agent_id: number;
    first: IndustryUsageItem[];
    second: IndustryUsageItem[];
  }>('/api/qual/industry-usage', { params: { agent_id: agentId } });

// 按行业使用次数对行业列表排序（接收后端聚合的 usage map，避免拉全量资质）
// level: 'first' 按一级行业统计；'second' 按二级行业统计
// 使用次数多的行业排在前面，方便优先选择常用行业
export const sortIndustriesByUsage = (
  industries: Industry[],
  usageMap: Record<string, number>,
  level: 'first' | 'second'
): Industry[] => {
  return [...industries].sort(
    (a, b) => (usageMap[String(b.id)] || 0) - (usageMap[String(a.id)] || 0)
  );
};

// 资质模版（整套：必要+补充+其他 一起存为一份模版）
export interface QualificationTemplateFileItem {
  type: number; // 0=必要, 1=补充, 2=其他
  name: string;
  files: { url: string; name: string; file_id?: string | number }[];
}

export interface QualificationTemplate {
  id: number;
  name: string;
  qual_type: number; // 后端字段：旧版固定传 0 表示「涵盖所有类型」
  files: QualificationTemplateFileItem[];
  description?: string | null;
  agent_id?: number | null;
  created_at: string;
}

export const listTemplates = (params: { keyword?: string; agent_id?: number } = {}) =>
  api.get<QualificationTemplate[]>('/api/template/list', { params });

export const addTemplate = (data: {
  name: string;
  qual_type: number; // 整套模版固定传 0
  files: QualificationTemplateFileItem[];
  description?: string;
  agent_id?: number;
}) => api.post<QualificationTemplate>('/api/template/add', data);

export const deleteTemplate = (templateId: number) =>
  api.delete<{ success: boolean; message: string }>(`/api/template/${templateId}`);

// 批量新增·完整投放预设（独立于资质模版）
export interface DeliveryPreset {
  id: number;
  name: string; // 自动命名：产品名称-二级行业-广告主名称-产品类型
  agent_id?: number | null;
  advertiser_name?: string | null;
  industry_first?: string | null;
  industry_first_name?: string | null;
  industry_second?: string | null;
  industry_second_name?: string | null;
  product_type?: number | null; // 1网址/2应用/3快应用/4小程序
  product_name?: string | null;
  package_name?: string | null;
  description?: string | null;
  icp_case_number?: string | null;
  icp_domain_name?: string | null;
  qualifications?: { url: string; type_id: number; type: number; name?: string }[] | null;
  created_at: string;
  updated_at: string;
}

export const listPresets = (params: {
  agent_id?: number;
  industry_second?: string;
  product_type?: number;
  keyword?: string;
} = {}) => api.get<DeliveryPreset[]>('/api/preset/list', { params });

export const addPreset = (data: Partial<DeliveryPreset> & { name: string }) =>
  api.post<DeliveryPreset>('/api/preset/add', data);

export const updatePreset = (presetId: number, data: Partial<DeliveryPreset> & { name: string }) =>
  api.put<DeliveryPreset>(`/api/preset/${presetId}`, data);

export const deletePreset = (presetId: number) =>
  api.delete<{ success: boolean; message: string }>(`/api/preset/${presetId}`);

export const batchDeletePresets = (ids: number[]) =>
  api.post<{ success: boolean; message: string }>('/api/preset/batch_delete', { ids });

// 认证（与 api/auth 保持一致，便于从 api 统一导入）
export { login } from './auth';

export interface TaskProgress {
  id: number;
  task_id: string;
  task_type: string;
  status: string;
  progress: number;
  message: string;
  result: string | null;
  created_at: string;
  updated_at: string;
}

export const getTasks = (skip: number = 0, limit: number = 20, type?: string) => {
  return api.get<TaskProgress[]>('/api/tasks', { params: { skip, limit, type } });
};

export const getTask = (taskId: string) => {
  return api.get<TaskProgress>(`/api/tasks/${taskId}`);
};

export const stopTask = (taskId: string) => {
  return api.post<{ success: boolean; message: string }>(`/api/tasks/${taskId}/stop`);
};

export const cleanupTasks = (days: number = 7) => {
  return api.post<{ success: boolean; message: string; deleted_count: number }>('/api/tasks/cleanup', null, {
    params: { days }
  });
};


export default api;
